import React, { useMemo } from "react";
import { DrawerHeader } from "@/components/ui/drawer";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Form, FormField } from "@/components/ui/form";
import { FormItem } from "@/components/ui/form";
import { FormLabel } from "@/components/ui/form";
import { FormControl } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Step } from "../expense-form/constants";
import { UseFormReturn } from "react-hook-form";
import { Currency } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface VendorPaymentDetailsForm {
  iban: string;
  bankName: string;
  swiftCode: string;
  accountHolderName: string;
  currencyId: string;
  paymentTerms?: string;
  bankAddress?: string;
  bankAccountNumber?: string;
  isDefault?: boolean;
}

const PaymentMethodFormStep = ({
  form,
  setStep,
  onSubmit,
  currencies,
}: {
  form: UseFormReturn<VendorPaymentDetailsForm>;
  setStep: React.Dispatch<React.SetStateAction<number>>; // AddVendorPaymentDetailsStep
  onSubmit: (data: VendorPaymentDetailsForm) => void;
  currencies: Currency[];
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <DrawerHeader
        title={t("title.enter_vendor_payment_methods")}
        description={t("description.enter_vendor_payment_methods")}
        leftButton={
          <button
            type="button"
            onClick={() => {
              setStep(Step.INITAL);
              form.reset();
            }}
          >
            <CaretLeft color="#E3EDFC" size={18} />
          </button>
        }
        rightButton={<span className="w-[18px]" />}
      />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) => {
            console.warn(errors);
          })}
        >
          <div className="space-y-5">
            <FormField
              control={form.control}
              name="iban"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-iban-input"
                      invalid={!!form.formState.errors.iban}
                      placeholder={t("label.enter_iban")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bankName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-bank-name-input"
                      invalid={!!form.formState.errors.bankName}
                      placeholder={t("label.enter_bank_name")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="swiftCode"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-swift-code-input"
                      invalid={!!form.formState.errors.swiftCode}
                      placeholder={t("label.enter_swift_code")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="accountHolderName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-account-holder-name-input"
                      invalid={!!form.formState.errors.accountHolderName}
                      placeholder={t("label.enter_account_holder_name")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <CurrencyButton
              currencies={currencies}
              form={form}
              setStep={setStep}
            />

            <FormField
              control={form.control}
              name="bankAddress"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-bank-address-input"
                      invalid={!!form.formState.errors.bankAddress}
                      placeholder={t("label.enter_bank_address")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bankAccountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-bank-account-number-input"
                      invalid={!!form.formState.errors.bankAccountNumber}
                      placeholder={t("label.enter_bank_account_number")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="paymentTerms"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      data-testid="add-vendor-payment-details-form-payment-terms-input"
                      invalid={!!form.formState.errors.paymentTerms}
                      placeholder={t("label.enter_payment_terms")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2">
                  <FormControl>
                    <Checkbox
                      data-testid="add-vendor-payment-details-form-is-default-checkbox"
                      invalid={!!form.formState.errors.isDefault}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel>{t("label.set_as_default")}</FormLabel>
                </FormItem>
              )}
            />

            <Button
              data-testid="add-vendor-payment-details-form-save-button"
              type="submit"
              className="w-full"
            >
              {t("button.save")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

const CurrencyButton = ({
  currencies,
  form,
  setStep,
}: {
  currencies: Currency[];
  form: UseFormReturn<VendorPaymentDetailsForm>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  const currency = useMemo(
    () =>
      currencies.find((currency) => currency.id === form.watch("currencyId")),
    [currencies, form]
  );

  return (
    <>
      {form.getValues("currencyId") ? (
        <button
          data-testid="add-vendor-payment-details-form-currency-button"
          type="button"
          onClick={() => setStep(Step.CURRENCY)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.currency")}
          </span>
          <p className="flex items-center gap-1.5 text-start text-[15px] text-white">
            <span>{currency?.symbol}</span>
            <span>{t(`currency.${currency?.code.toLocaleLowerCase()}`)}</span>
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.currencyId}
          data-testid="add-vendor-payment-details-form-currency-button"
          onClick={() => setStep(Step.CURRENCY)}
          size="lg"
          variant="outline"
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-lg border-none border-white/30 bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg",
            {
              "border-solid border-red-500 bg-[#E86363]/15 hover:border-red-500 active:border-red-500/30":
                form.formState.errors.currencyId,
            }
          )}
        >
          {t("label.currency")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

export default PaymentMethodFormStep;
